/**
 * Artifact-version mutation hook factory.
 *
 * Produces a `useCreateArtifactVersion()` hook that issues a TanStack Query
 * mutation against an injected `createArtifact` callback. Keeps the entity
 * layer free of any value-import from `@/features/`, `@/pages/`, or `@/app/`.
 *
 * **Dep-inversion rationale**: the previous implementation
 * (`features/widgets/hooks/useCreateArtifactVersion/useCreateArtifactVersion.ts`)
 * value-imported `createNewArtifact` from
 * `@/features/artifacts/api/artifacts.api` (features layer) — a layer-order
 * violation when consumed by an entity. The persistence backend
 * is now an injected callback supplied at the features-layer wiring file
 * `features/widgets/hooks/useCreateArtifactVersion/index.ts`.
 *
 * **Behavior preserved**: identical to the previous implementation —
 * same TanStack `useMutation` shape, same `mutationFn` payload mapping
 * (`chatId → contextId`, `isWidget: true` flag), same error/result types.
 */
import { useMutation } from '@tanstack/react-query';
import type { ArtifactVersionDto } from '@inference.engine/api-client/web';
import type { CreateArtifactVersionParams } from './useCreateArtifactVersion.types';

/**
 * Persistence callback shape required by `makeUseCreateArtifactVersion`.
 * The features-layer wiring binds the concrete `createNewArtifact` from
 * `@/features/artifacts/api/artifacts.api`.
 */
export type CreateArtifactCallback = (payload: {
  contextId: string;
  exchangeId?: string;
  key: string;
  name: string;
  kind: CreateArtifactVersionParams['kind'];
  data: { [key: string]: unknown };
  isWidget: true;
}) => Promise<ArtifactVersionDto>;

/**
 * Build a `useCreateArtifactVersion` hook bound to the supplied artifact
 * persistence callback.
 */
export const makeUseCreateArtifactVersion = (createArtifact: CreateArtifactCallback) => {
  return () => {
    return useMutation<ArtifactVersionDto, Error, CreateArtifactVersionParams>({
      mutationFn: ({ chatId, exchangeId, key, name, kind, data }) =>
        createArtifact({
          contextId: chatId,
          exchangeId,
          key,
          name,
          kind,
          data: data as { [key: string]: unknown },
          isWidget: true,
        }),
    });
  };
};
